import React, { useRef, useState, useEffect } from 'react';
import {
    Alert,
    Button,
    Divider,
    Form,
    Col,
    Modal,
    Popconfirm,
    Row,
    Flex,
    Select,
    DatePicker,
    Tooltip,
} from 'antd';
import { CheckCircleOutlined, CopyOutlined } from '@ant-design/icons';
import TextArea from 'antd/lib/input/TextArea';
import * as adminactions from "store/actions/AuthActions";
import dayjs from 'dayjs';

import TemplateComposer, { contactNumber } from "components/Leads/TemplateComposer";
import { LEAD_STATUS_OPTIONS, leadStatusMeta } from "components/Leads/leadStatus";
import { notify } from "components";

// Activity types that can carry an outgoing message. Picking one of these on a
// new activity opens the template composer below the form.
const SENDABLE_TYPES = { EMAIL: 'EMAIL', WHATSAPP: 'WHATSAPP' };

/**
 * Add or edit one activity on a lead or an account. `recordType` decides which
 * key the API is given; everything else is identical for both.
 */
const LeadActivityForm = ({
    leadId,
    lead,
    recordType = 'lead',
    leadActivities,
    setLeadActivities,
    instance,
    form,
    reload,
    onLeadStatusChange,
}) => {

    const activity_type_options = [
        { value: 'WHATSAPP', label: 'WHATSAPP' },
        { value: 'EMAIL', label: 'EMAIL' },
        { value: 'CALL', label: 'CALL' },
        { value: 'OTHER', label: 'OTHER' },
    ]

    const status_options = LEAD_STATUS_OPTIONS.map(({ value, label }) => ({ value, label }));

    const [modal, modalContextHolder] = Modal.useModal();
    const composerRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [templatePicked, setTemplatePicked] = useState(false);

    const activityType = Form.useWatch('activity_type', form);
    // Only a new activity can send - editing an old one just edits the record.
    const channel = !instance ? SENDABLE_TYPES[activityType] : undefined;

    // Whether this record can be reached *through the CRM*.
    const reachable = channel === 'EMAIL' ? lead?.email : lead?.wa_number;
    // A WhatsApp message can always be sent by hand from the rep's own phone,
    // so the number the record does carry is enough to pick a template, copy
    // the message and log it.
    const markNumber = channel === 'WHATSAPP' ? contactNumber(lead) : lead?.email;
    const canCompose = !!(reachable || markNumber);

    /**
     * Once a lead activity is saved, offer to carry its status onto the lead
     * itself. Resolves when the user has answered, so the drawer stays open
     * until then. Accounts have no status, and an unchanged one isn't asked.
     */
    const offerLeadStatusUpdate = (status) => {
        if (recordType !== 'lead' || !status || status === lead?.status) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            modal.confirm({
                title: 'Update lead status?',
                content: `Do you want to update the lead status to "${leadStatusMeta(status).label}" as well?`,
                okText: 'Yes, update',
                cancelText: 'No',
                onOk: async () => {
                    try {
                        await adminactions.updateLeadStatus(leadId, status);
                        onLeadStatusChange?.(status);
                        notify('success', 'Lead status updated');
                    } catch (err) {
                        notify('error', 'Could not update the lead status');
                    }
                    resolve();
                },
                onCancel: () => resolve(),
            });
        });
    };

    const onFinish = async (values) => {
        setSaving(true);
        // With a template chosen the send endpoint creates the LeadActivity
        // itself, so don't also post one here. Without a way to reach the
        // record it falls through and logs a plain activity instead - use
        // "Mark as sent" to log the message itself.
        if (channel && reachable && composerRef.current?.hasTemplate()) {
            const { sent } = await composerRef.current.send({
                activityStatus: values.status,
                notes: values.notes,
            });
            setSaving(false);
            if (sent) {
                await offerLeadStatusUpdate(values.status);
                reload();
            }
            return;
        }

        const payload = { ...values, [recordType]: leadId };
        if (instance) {
            const response = await adminactions.updateLeadActivity(payload, instance.id);
            if (response) {
                const restAct = leadActivities.filter((act) => act.id !== response.data['id'])
                setLeadActivities([...restAct, response.data])
                setSaving(false);
                await offerLeadStatusUpdate(values.status);
                reload();
            }
        } else {
            const response = await adminactions.addLeadActivity(payload);
            if (response) {
                setLeadActivities((leadActivities) => [...leadActivities, response.data])
                setSaving(false);
                await offerLeadStatusUpdate(values.status);
                reload();
            }
        }
        setSaving(false);
    };

    /**
     * Log the message as already sent, from a phone or mailbox outside the
     * CRM. Same record as a real send minus the delivery, so the timeline
     * shows what went out.
     */
    const markAsSent = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (err) {
            return;
        }
        setSaving(true);
        const result = await composerRef.current?.send({
            activityStatus: values.status,
            notes: values.notes,
            markAsSent: true,
        });
        setSaving(false);
        if (result?.sent) {
            await offerLeadStatusUpdate(values.status);
            reload();
        }
    };

    useEffect(() => {
        if (instance) {
            const thisInstance = { ...instance }
            let date_fields = ['activity_date']
            for (let field of date_fields) {
                thisInstance[field] = thisInstance[field] ? dayjs(thisInstance[field]) : null;
            }
            form.setFieldsValue(thisInstance);
        }
    }, [instance])

    return (
        <Form
            form={form}
            labelCol={{ span: 5 }}
            wrapperCol={{ span: 18 }}
            onFinish={onFinish}
        >
            {modalContextHolder}
            <Row style={{ margin: "15px" }}>
                <Col span={24} style={{}}>
                    <div className="site-card-border-less-wrapper">
                        <div>
                            <Form.Item label="Activity Type" name='activity_type' rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select Activity Type"
                                    options={activity_type_options}
                                />
                            </Form.Item>
                            <Form.Item label="Date" name="activity_date">
                                <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" />
                            </Form.Item>
                            <Form.Item label="Status" name='status' rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select Status"
                                    options={status_options}
                                />
                            </Form.Item>
                            <Form.Item label="Notes" name='notes'>
                                <TextArea />
                            </Form.Item>
                        </div>

                        {channel && (
                            <>
                                <Divider orientation="left" plain>
                                    {channel === 'EMAIL' ? 'Send an email' : 'Send a WhatsApp message'}
                                </Divider>
                                {!canCompose ? (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message={
                                            channel === 'EMAIL'
                                                ? 'This record has no email address, so there is nothing to send to. Saving still logs the activity.'
                                                : 'This record has no phone or WhatsApp number, so there is nothing to send to. Saving still logs the activity.'
                                        }
                                    />
                                ) : (
                                    <>
                                        <Alert
                                            type="info"
                                            showIcon
                                            style={{ marginBottom: 12 }}
                                            message={
                                                reachable
                                                    ? 'Optional — pick a template to send one now. Leave it empty to just log the activity.'
                                                    : `No WhatsApp number on this record, so nothing can go out from here. Pick a template, copy the message, send it from your own phone and mark it as sent — it is logged against ${markNumber}.`
                                            }
                                        />
                                        <TemplateComposer
                                            ref={composerRef}
                                            channel={channel}
                                            leads={lead ? [lead] : []}
                                            recordType={recordType}
                                            onTemplateChange={(chosen) => setTemplatePicked(!!chosen)}
                                            compact
                                        />
                                    </>
                                )}
                            </>
                        )}

                        <Flex justify='flex-end' gap="small" wrap="wrap" style={{ marginTop: 16 }}>
                            {channel && canCompose && templatePicked && (
                                <>
                                    <Tooltip title="Copy the rendered message">
                                        <Button
                                            icon={<CopyOutlined />}
                                            onClick={() => composerRef.current?.copyMessage()}
                                        >
                                            Copy message
                                        </Button>
                                    </Tooltip>
                                    <Popconfirm
                                        title="Mark as sent"
                                        description={`Log this ${channel === 'EMAIL' ? 'email' : 'message'} as already sent to ${markNumber}? Nothing will be sent from here.`}
                                        okText="Mark as sent"
                                        onConfirm={markAsSent}
                                    >
                                        <Button icon={<CheckCircleOutlined />} loading={saving}>
                                            Mark as sent
                                        </Button>
                                    </Popconfirm>
                                </>
                            )}
                            <Button type="primary" htmlType="submit" loading={saving} style={{ minWidth: 120 }} >
                                {channel && reachable && templatePicked ? 'Save & Send' : 'Save'}
                            </Button>
                        </Flex>
                    </div>
                </Col>
            </Row>
        </Form>
    );
};
export default LeadActivityForm;
